package fr.frux.shared.domain.model

import kotlin.time.Instant

/**
 * Domain model for a fruit tree.
 * This is the business logic representation, different from the DTO.
 */
data class Tree(
    val id: Long,
    val species: SpeciesRef,
    val latitude: Double,
    val longitude: Double,
    val status: TreeStatus,
    val description: String?,
    // Thumb du héro — alimenté pour les listes et la carte. Le détail complet vient de [photos].
    val photoUrl: String?,
    // Détail seulement ; liste vide (jamais nulle) quand le transport ne l'a pas renseigné.
    val photos: List<TreePhoto> = emptyList(),
    val createdBy: Long,
    val createdByUsername: String? = null, // Pseudo du créateur, renseigné sur le détail (null ailleurs)
    val createdAt: Long,
    val updatedAt: Long,
    // Traits posés par les cueilleurs (accès difficile, délicieux), avec compteur et indicateur
    // personnel. Liste vide (jamais nulle) quand le transport ne l'a pas renseignée.
    val traits: List<TreeTrait> = emptyList(),
    // Dernière observation de statut, tous auteurs confondus. `null` tant qu'aucune observation
    // n'a été enregistrée.
    val latestObservation: TreeObservation? = null,
    // Passage humain date (ADR 0025). Renseigne aussi sur les listes et le cache, a la difference
    // de [latestObservation] qui reste du detail. `null` = aucun humain n'a jamais rien observe.
    val lastObservedAt: Instant? = null,
    // Dernière observation de statut de l'utilisateur courant. `null` s'il n'a jamais observé
    // cet arbre.
    val myObservationStatus: ObservableTreeStatus? = null,
    // Nullable lorsqu'aucune fenetre de recolte n'est declaree dans le referentiel.
    val harvestPart: HarvestPart? = null,
    val certainty: Certainty = Certainty.PROBABLE,
    // Favori de l'utilisateur courant. False pour un visiteur anonyme comme pour les listes
    // cartographiques, qui ne portent pas l'information.
    val isFavorite: Boolean = false,
    // Signalement par les pairs (#129 lot B, ADR 0015). Dérivé serveur de `disputed_at`, jamais de
    // `certainty` : la déclaration du créateur n'est jamais réécrite par un signalement. Même
    // vocabulaire visuel que TO_CONFIRM côté carte (pastille "?").
    val disputed: Boolean = false,
    // Kinds des signalements encore ouverts (#208), détail seulement — vide sur les listes et sur
    // le cache hors ligne, où le bandeau retombe sur son libellé générique. Chaînes du contrat
    // (`MISIDENTIFIED`, `GONE`), un kind inconnu s'ignore à l'affichage.
    val disputedKinds: List<String> = emptyList(),
    // `true` si l'utilisateur courant a un signalement MISIDENTIFIED encore ouvert sur cet arbre.
    // Renseigné sur le détail seulement (même portée que `isFavorite`).
    val reportedByMe: Boolean = false,
    // Même dérivation, pour le kind GONE (#129 lot C, ADR 0015). `reportedByMe` reste scopé à
    // MISIDENTIFIED (contrat déjà publié par le lot B) plutôt que renommé ou généralisé — un
    // booléen jumeau, même patron que `isFavorite`.
    val reportedGoneByMe: Boolean = false,
    // Dernière correction d'identité visible (détail et mutations). `null` : aucune, ou API sans cette donnée.
    val lastCorrection: TreeCorrectionMark? = null,
    // Lieu de cueillette auquel l'arbre est rattaché (ADR 0035). `null` : arbre libre.
    val siteId: Long? = null,
)

/** Statuts qu'un cueilleur peut observer sur le terrain (`UNAVAILABLE`/`UNKNOWN` en sont exclus). */
enum class ObservableTreeStatus { FLOWERING, RIPE_READY, PICKED_CLEAN }

/** Type de trait posable sur un arbre. */
enum class TreeTraitKind { ACCESS_DIFFICULT, DELICIOUS }

/** Observation de statut : qui, quand, quel statut — dernière en date ou celle de l'appelant. */
data class TreeObservation(
    val status: ObservableTreeStatus,
    val observedAtMillis: Long,
    val observerUsername: String?,
)

/** Résumé d'un trait posé sur l'arbre : compteur agrégé et indicateur personnel. */
data class TreeTrait(
    val kind: TreeTraitKind,
    val count: Int,
    val mine: Boolean,
)

/** Photo attachée à un arbre (#223, lot A photos). [createdAt] : epoch millis. */
data class TreePhoto(
    val id: String,
    val fullUrl: String,
    val thumbUrl: String,
    val width: Int,
    val height: Int,
    val authorId: Long,
    val createdAt: Long,
)
